<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">Goal/Gap</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>
    <b-container fluid class="mt--6">
      <upgrade-overlay :title="'Goal/Gap'" :upgradeImg="'overlays/goal-gap.png'" :placeholderImg="'placeholders/goal-gap.png'" v-if="!subscribed"></upgrade-overlay>
      <div v-else>
        <b-collapse visible id="filters">
          <b-card>
            <b-row>
              <b-col>
                <base-input label="Year" label-for="yearInput">
                  <el-select v-model="year">
                    <el-option v-for="year in years"
                               :key="year.label"
                               :label="year.label"
                               :value="year.value">
                    </el-option>
                  </el-select>
                </base-input>
              </b-col>
            </b-row>
          </b-card>
        </b-collapse>
        <loading-panel v-if="loading" class="mt-6"></loading-panel>
        <div id="goal-gap" v-else>
          <old-days-left :daysLeft="daysLeft"></old-days-left>
          <b-row>
            <b-col md="6">
              <old-listing-stats :actuals="actuals" :items="items"></old-listing-stats>
            </b-col>
            <b-col md="6">
              <old-transaction-stats :actuals="actuals" :items="items" :year="year"></old-transaction-stats>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-card no-body>
                <b-card-header>
                  <h3 class="mb-0">GAP TO HIT QUARTERLY GOALS</h3>
                </b-card-header>
                <b-table responsive small :items="itemsArray(quarter)" :fields="gapFields">
                  <template #cell(quarter)="data">
                    <strong>{{ data.item.quarter }}</strong>
                  </template>

                  <template #cell(closings)="data">
                    <span v-if="closings(data.item.seasonality) > 0">
                      {{ closings(data.item.seasonality) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(volume)="data">
                    <span v-if="gci(data.item.seasonality) > 0">
                      {{ volume(data.item.seasonality) | currency }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(gci)="data">
                    <span v-if="gci(data.item.seasonality) > 0">
                      {{ gci(data.item.seasonality) | currency }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(appts_held)="data">
                    <span v-if="apptsHeld(items[listingsQuarter].seasonality) > 0">
                      {{ apptsHeld(items[listingsQuarter].seasonality) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(listings_taken)="data">
                    <span v-if="Math.round(totalListingsTaken * (items[listingsQuarter].seasonality / 100)) - actuals.stats[listingsQuarter].listings_taken > 0">
                      {{ Math.round(totalListingsTaken * (items[listingsQuarter].seasonality / 100)) - actuals.stats[listingsQuarter].listings_taken }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>
                </b-table>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-card no-body>
                <b-card-header>
                  <h3 class="mb-0">DAILY GAP NEEDED TO STAY ON TRACK</h3>
                </b-card-header>
                <b-table responsive small :items="itemsArray(quarter)" :fields="gapFields">
                  <template #cell(closings)="data">
                    <span v-if="closings(data.item.seasonality) > 0">
                      {{ (closings(data.item.seasonality) / actuals.days_left).toFixed(2) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(volume)="data">
                    <span v-if="volume(data.item.seasonality) > 0">
                      {{ volume(data.item.seasonality) / actuals.days_left | currency }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(gci)="data">
                    <span v-if="gci(data.item.seasonality) > 0">
                      {{ gci(data.item.seasonality) / actuals.days_left | currency }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(appts_held)="data">
                    <span v-if="apptsHeld(items[listingsQuarter].seasonality) > 0">
                      {{ (apptsHeld(items[listingsQuarter].seasonality) / actuals.stats[listingsQuarter].days_left).toFixed(2) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(listings_taken)="data">
                    <span v-if="listingsTaken(items[listingsQuarter].seasonality) > 0">
                      {{ (listingsTaken(items[listingsQuarter].seasonality) / actuals.stats[listingsQuarter].days_left).toFixed(2) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>
                </b-table>
              </b-card>
            </b-col>
            <b-col md="6">
              <b-card no-body>
                <b-card-header>
                  <h3 class="mb-0">WEEKLY GAP NEEDED TO STAY ON TRACK</h3>
                </b-card-header>
                <b-table responsive small :items="itemsArray(quarter)" :fields="gapFields">
                  <template #cell(quarter)="data">
                    <strong>{{ data.item.quarter }}</strong>
                  </template>

                  <template #cell(closings)="data">
                    <span v-if="actuals.days_left < 7">
                      <b-badge variant="danger">&lt; 1 Week in Quarter</b-badge>
                    </span>
                    <span v-else-if="closings(data.item.seasonality) > 0">
                      {{ ((closings(data.item.seasonality) / actuals.days_left) * 7).toFixed(2) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(volume)="data">
                    <span v-if="actuals.days_left < 7">
                      <b-badge variant="danger">&lt; 1 Week in Quarter</b-badge>
                    </span>
                    <span v-else-if="volume(data.item.seasonality) > 0">
                      {{ (volume(data.item.seasonality) / actuals.days_left) * 7 | currency }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(gci)="data">
                    <span v-if="actuals.days_left < 7">
                      <b-badge variant="danger">&lt; 1 Week in Quarter</b-badge>
                    </span>
                    <span v-else-if="gci(data.item.seasonality) > 0">
                      {{ (gci(data.item.seasonality) / actuals.days_left) * 7 | currency }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(appts_held)="data">
                    <span v-if="apptsHeld(items[listingsQuarter].seasonality) > 0">
                      {{ (apptsHeld(items[listingsQuarter].seasonality) / actuals.stats[listingsQuarter].days_left * 7).toFixed(2) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>

                  <template #cell(listings_taken)="data">
                    <span v-if="listingsTaken(items[listingsQuarter].seasonality) > 0">
                      {{ (listingsTaken(items[listingsQuarter].seasonality) / actuals.stats[listingsQuarter].days_left * 7).toFixed(2) }}
                    </span>
                    <b-badge variant="success" v-else>Complete</b-badge>
                  </template>
                </b-table>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-container>
    <b-modal id="no-economic-model" title="Missing Economic Model" hide-footer>
      <p class="mb-4 text-center">You are missing an Economic Model for {{ year }}</p>
      <b-button variant="danger" block :to="'/business-planning/economic-model/' + year + '/edit'">Create Economic Model</b-button>
    </b-modal>
  </div>
</template>
<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import {Select, Option} from 'element-ui'
import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import LoadingPanel from '@/components/LoadingPanel';
import OldDaysLeft from './GoalGap/OldDaysLeft';
import OldListingStats from './GoalGap/OldListingStats';
import OldTransactionStats from './GoalGap/OldTransactionStats';
import economicModelFormData from '@/views/Components/Forms/FormData/economicModelFormData';
import cosFormData from '@/views/Components/Forms/FormData/cosFormData';
import UpgradeOverlay from '@/views/Components/Overlays/UpgradeOverlay';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    RouteBreadCrumb,
    LoadingPanel,
    OldDaysLeft,
    OldListingStats,
    OldTransactionStats,
    UpgradeOverlay,
  },
  data() {
    return {
      loading: true,
      year: new Date().getFullYear(),
      actuals: {
        quarter: null,
        listings_quarter: null,
        days_left: null,
        listings_days: null,
        end_date: null,
        sales_start: null,
        sales_end: null,
        stats: null
      },
      gapFields: [
        {
          key: 'appts_held',
          label: 'Listings Held'
        },
        {
          key: 'listings_taken',
          label: 'Listings Taken'
        },
        {
          key: 'closings',
          label: 'Sales Needed'
        },
        {
          key: 'volume'
        },
        {
          key: 'gci',
          label: 'GCI'
        }
      ],
      items: [
        {quarter: 'Q1', seasonality: 0, title: 'What % of your closings will occur in Q1 (January - March)'},
        {quarter: 'Q2', seasonality: 0, title: 'What % of your closings will occur in Q2 (April - June)'},
        {quarter: 'Q3', seasonality: 0, title: 'What % of your closings will occur in Q3 (July - September)'},
        {quarter: 'Q4', seasonality: 0, title: 'What % of your closings will occur in Q4 (October - December)'}
      ],
      years: [
        { value: new Date().getFullYear() - 6, label: new Date().getFullYear() - 6 },
        { value: new Date().getFullYear() - 5, label: new Date().getFullYear() - 5 },
        { value: new Date().getFullYear() - 4, label: new Date().getFullYear() - 4 },
        { value: new Date().getFullYear() - 3, label: new Date().getFullYear() - 3 },
        { value: new Date().getFullYear() - 2, label: new Date().getFullYear() - 2 },
        { value: new Date().getFullYear() - 1, label: new Date().getFullYear() - 1 },
        { value: new Date().getFullYear(), label: new Date().getFullYear() },
        { value: new Date().getFullYear() + 1, label: new Date().getFullYear() + 1 },
        { value: new Date().getFullYear() + 2, label: new Date().getFullYear() + 2 },
        { value: new Date().getFullYear() + 3, label: new Date().getFullYear() + 3 },
      ],
      daysLeft: {
        quarter: null,
        listings_quarter: null,
        days_left: null,
        listings_days: null,
        end_date: null,
        sales_start: null,
        sales_end: null,
        stats: null
      }
    }
  },
  computed: {
    ...mapGetters({
      user: 'profile/user',
      userRoute: 'profile/userRoute',
      subBasic: 'profile/subBasic',
      subStandard: 'profile/subStandard',
      subPremium: 'profile/subPremium',
      economicModel: 'economicModel/economicModel',
      closedUnits: 'economicModel/closedUnits',
      closedSalesVolume: 'economicModel/closedSalesVolume',
      grossProfit: 'economicModel/grossProfit',
      companyGci: 'economicModel/companyGci',
      totalApptsHeld: 'economicModel/totalApptsHeld',
      totalListingsTaken: 'economicModel/totalListingsTaken',
      roles: 'roles/roles',
    }),
    subscribed() {
      return this.subBasic + this.subStandard || this.subPremium;
    },
    quarter() {
      return this.actuals.quarter - 1;
    },
    listingsQuarter() {
      return this.actuals.listings_quarter - 1;
    },
    owner() {
      return this.roles.includes("owner");
    },
    userGci() {
      return this.owner ? this.companyGci : this.grossProfit;
    }
  },
  methods: {
    async getActuals() {
      axios.get(this.userRoute + "/goal-gap?year=" + this.year)
        .then(response => {
          this.actuals = response.data;
          this.loading = false;
        });
    },
    async getDaysLeft() {
      axios.get(this.userRoute + "/goal-gap/days-left")
        .then(response => {
          this.daysLeft = response.data;
        });
    },
    syncSeasonality() {
      this.items[0].seasonality = this.economicModel.seasonality_q1;
      this.items[1].seasonality = this.economicModel.seasonality_q2;
      this.items[2].seasonality = this.economicModel.seasonality_q3;
      this.items[3].seasonality = this.economicModel.seasonality_q4;
    },
    itemsArray(index) {
      return [this.items[index]];
    },
    closings(seasonality) {
      let closings = Math.round(this.closedUnits * (seasonality / 100)) - this.actuals.stats[this.quarter].closings;
      
      return (closings < 0) ? 0 : closings;
    },
    volume(seasonality) {
      let volume = (this.closedSalesVolume * (seasonality / 100) - this.actuals.stats[this.quarter].volume);

      return (volume < 0) ? 0 : volume;
    },
    gci(seasonality) {
      let gci = this.userGci * (seasonality / 100) - this.actuals.stats[this.quarter].gci;

      return (gci < 0) ? 0 : gci;
    },
    apptsHeld(seasonality) {
      let apptsHeld = Math.round(this.totalApptsHeld * (seasonality / 100)) - this.actuals.stats[this.listingsQuarter].listings_held;

      return (apptsHeld < 0) ? 0 : apptsHeld;
    },
    listingsTaken(seasonality) {
      let listingsTaken = Math.round(this.totalListingsTaken * (seasonality / 100)) - this.actuals.stats[this.listingsQuarter].listings_taken;

      return (listingsTaken < 0) ? 0 : listingsTaken;
    },
    checkEconomicModel() {
      if(this.economicModel.id == null) {
        this.$bvModal.show('no-economic-model');
      }

      this.syncSeasonality();
      this.getDaysLeft();
      this.getActuals();
    },
    async getContractYear()
    {
      await axios.get(this.userRoute + '/team/year')
      .then(response => {
        this.year = response.data;
        this.checkEconomicModel();
      })
      .catch(error => {
        // 
      });
    }
  },
  created() {
    this.getContractYear();
  },
  mounted() {
    document.title = this.year + ' Goal/Gap';
  },
  watch: {
    year(to, from) {
      this.$store.dispatch('economicModel/get', {user: this.user.slug, year: to})
      .then(() => {
        this.checkEconomicModel();
      });
    }
  }
};
</script>